import { useState } from "react";
import { Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCallTimer, formatCallDuration } from "@/stores/callTimer.store";
import { useCallElapsedSeconds } from "@/hooks/useCallElapsedSeconds";
import { StartCallDialog } from "./StartCallDialog";
import type { EmbeddedContact } from "@/api/types";

interface StartCallButtonProps {
  /** Project slug — stored on the timer snapshot so consumers
   *  elsewhere in the app know which company the call belongs to
   *  when navigating back from the global banner. */
  projectSlug: string;
  companyId: number;
  companyName: string;
  contacts: EmbeddedContact[];
}

/**
 * Toolbar button with two visual states:
 *
 *  - inactive: emerald "Start call" button. Clicking it opens
 *    StartCallDialog so the user can confirm which contact they're
 *    calling before the timer starts ticking.
 *  - active: pulsing emerald pill showing the contact's name + a
 *    live timer + a Stop button. Stopping stages a `pendingLog`
 *    payload on the store; CompanyDetailPage picks it up and opens
 *    the LogCallDialog pre-filled with the measured duration.
 *
 * The button shows the active shape regardless of which company the
 * timer is running against — a user on company A's page who started
 * a call from company B still sees the timer here, mirroring the
 * global banner. (V1 only allows one call at a time.)
 */
export function StartCallButton({
  projectSlug,
  companyId,
  companyName,
  contacts,
}: StartCallButtonProps) {
  const active = useCallTimer((s) => s.active);
  const activeContactName = useCallTimer((s) => s.contactName);
  const startCall = useCallTimer((s) => s.startCall);
  const stopCall = useCallTimer((s) => s.stopCall);
  const elapsed = useCallElapsedSeconds();
  const [pickerOpen, setPickerOpen] = useState(false);

  if (active) {
    return (
      <div
        className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 shadow-sm"
        role="status"
        aria-live="polite"
      >
        <span
          className="h-2 w-2 animate-pulse rounded-full bg-emerald-500"
          aria-hidden
        />
        <span className="truncate text-xs font-medium text-emerald-800">
          {activeContactName || "On a call"}
        </span>
        <span className="font-mono text-sm font-semibold tabular-nums text-emerald-900">
          {formatCallDuration(elapsed)}
        </span>
        <button
          type="button"
          onClick={() => stopCall()}
          aria-label="Stop call"
          className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-500 text-white transition-colors hover:bg-rose-600"
        >
          <Square className="h-3 w-3 fill-current" />
        </button>
      </div>
    );
  }

  return (
    <>
      <Button
        size="sm"
        onClick={() => setPickerOpen(true)}
        className={cn(
          "rounded-full px-4",
          "bg-emerald-600 text-white hover:bg-emerald-700",
        )}
      >
        <Play className="mr-2 h-3.5 w-3.5 fill-current" />
        Start call
      </Button>
      <StartCallDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        companyName={companyName}
        contacts={contacts}
        onStart={(contact) => {
          startCall({
            slug: projectSlug,
            companyId,
            companyName,
            contactId: contact.id,
            contactName: contact.full_name,
            contactEmail: contact.email,
            contactPhone: contact.phone,
          });
          setPickerOpen(false);
        }}
      />
    </>
  );
}
